import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixVeloSecretsVaultV1SecretsVaultServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'service.wix-code.com': [
      {
        srcPath: '/_api/cloud-secrets-vault-server',
        destPath: '',
      },
      {
        srcPath: '/_api/secrets-service',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/cloud-secrets-vault-server',
        destPath: '',
      },
      {
        srcPath: '/_api/secrets-service',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/cloud-secrets-vault-server',
        destPath: '',
      },
      {
        srcPath: '/_api/secrets-service',
        destPath: '',
      },
    ],
    'service-int.wix-code.com': [
      {
        srcPath: '/_api/cloud-secrets-vault-server',
        destPath: '',
      },
      {
        srcPath: '/_api/secrets-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/cloud-secrets-vault-server',
        destPath: '',
      },
      {
        srcPath: '/secrets-service',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_secrets_secrets';

/**
 * Retrieves a secret value by name.
 *
 * <blockquote class="caution">
 * __Caution:__
 * Only use a secret's value in the backend code. Returning the secret value in the frontend is a security risk.
 * </blockquote>
 */
export function getSecretValue(payload: object): RequestOptionsFactory<any> {
  function __getSecretValue({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.velo.secrets_vault.v1.secret',
      method: 'GET' as any,
      methodFqn: 'wix.velo.secrets_vault.v1.SecretsVaultService.GetSecretValue',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixVeloSecretsVaultV1SecretsVaultServiceUrl({
        protoPath: '/api/v1/secrets/name/{name}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getSecretValue;
}

/**
 * Retrieves a list of secrets.
 *
 * > **Note:** This method doesn't return the secret's value for security reasons. To retrieve the value, call [Get Secret Value](https://dev.wix.com/docs/rest/business-management/secrets/get-secret-value).
 */
export function listSecretInfo(payload: object): RequestOptionsFactory<any> {
  function __listSecretInfo({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.velo.secrets_vault.v1.secret',
      method: 'GET' as any,
      methodFqn: 'wix.velo.secrets_vault.v1.SecretsVaultService.ListSecretInfo',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixVeloSecretsVaultV1SecretsVaultServiceUrl({
        protoPath: '/api/v1/secrets',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'secrets.createdDate' },
              { path: 'secrets.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listSecretInfo;
}

/** Creates a secret. */
export function createSecret(payload: object): RequestOptionsFactory<any> {
  function __createSecret({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'secret.createdDate' }, { path: 'secret.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.velo.secrets_vault.v1.secret',
      method: 'POST' as any,
      methodFqn: 'wix.velo.secrets_vault.v1.SecretsVaultService.CreateSecret',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixVeloSecretsVaultV1SecretsVaultServiceUrl({
        protoPath: '/api/v1/secrets',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __createSecret;
}

/**
 * Deletes a secret.
 *
 * <blockquote class="warning">
 * <strong>Warning:</strong>
 * Deleting a secret is irreversible and will break all code using the secret.
 * </blockquote>
 */
export function deleteSecret(payload: object): RequestOptionsFactory<any> {
  function __deleteSecret({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.velo.secrets_vault.v1.secret',
      method: 'DELETE' as any,
      methodFqn: 'wix.velo.secrets_vault.v1.SecretsVaultService.DeleteSecret',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixVeloSecretsVaultV1SecretsVaultServiceUrl({
        protoPath: '/api/v1/secrets/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteSecret;
}

/**
 * Updates 1 or all fields of a secret.
 *
 * To get the secret ID, call [List Secret Info](https://dev.wix.com/docs/rest/business-management/secrets/list-secret-info).
 */
export function partiallyUpdateSecret(
  payload: object
): RequestOptionsFactory<any> {
  function __partiallyUpdateSecret({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'secret.createdDate' }, { path: 'secret.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.velo.secrets_vault.v1.secret',
      method: 'PATCH' as any,
      methodFqn:
        'wix.velo.secrets_vault.v1.SecretsVaultService.PartiallyUpdateSecret',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixVeloSecretsVaultV1SecretsVaultServiceUrl({
        protoPath: '/api/v1/secrets/{id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __partiallyUpdateSecret;
}
